import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Alert,
  Chip,
} from '@mui/material';
import { NotificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import {
  NotificationType,
  NotificationPriority,
} from '../../types/user';

/**
 * NotificationTester - Debug component for testing notifications
 *
 * REMOVE THIS COMPONENT BEFORE PRODUCTION!
 * This is a temporary testing tool for Phase 1 implementation.
 */
export const NotificationTester: React.FC = () => {
  const { authState } = useAuth();
  const user = authState.user;

  const [notificationType, setNotificationType] = useState<NotificationType>('welcome');
  const [priority, setPriority] = useState<NotificationPriority>(NotificationPriority.NORMAL);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');

  const notificationTypes: NotificationType[] = [
    'welcome',
    'approval_granted',
    'approval_denied',
    'role_changed',
    'content_approved',
    'content_rejected',
    'comment_mention',
    'comment_reply',
    'photo_tagged',
    'research_note_assigned',
    'birthday_reminder',
    'system_announcement',
  ];

  const priorities = [
    NotificationPriority.LOW,
    NotificationPriority.NORMAL,
    NotificationPriority.HIGH,
    NotificationPriority.CRITICAL,
  ];

  const handleCreateNotification = async () => {
    if (!user) {
      setError('You must be logged in to create notifications');
      return;
    }

    try {
      setError('');
      setResult('');

      const notification = await NotificationService.createNotification({
        userId: user.uid,
        type: notificationType,
        title: title || undefined, // Auto-generate if empty
        message: message || undefined, // Auto-generate if empty
        priority,
        actionUrl: actionUrl || undefined,
        actionLabel: actionUrl ? 'View' : undefined,
      });

      setResult(`✅ Notification created successfully!\nID: ${notification.id}\nTitle: ${notification.title}\nMessage: ${notification.message}`);
    } catch (err: any) {
      setError(`❌ Error: ${err.message}`);
      console.error('Notification creation error:', err);
    }
  };

  const handleCreateMultiple = async () => {
    if (!user) {
      setError('You must be logged in to create notifications');
      return;
    }

    try {
      setError('');
      setResult('Creating multiple test notifications...');

      const testNotifications = [
        {
          type: 'welcome' as NotificationType,
          priority: NotificationPriority.NORMAL,
          title: 'Welcome to the Family!',
          message: 'We\'re excited to have you explore our family history.',
        },
        {
          type: 'comment_mention' as NotificationType,
          priority: NotificationPriority.HIGH,
          title: 'Someone mentioned you!',
          message: 'John Martz mentioned you in a comment on Sarah\'s profile.',
          actionUrl: '/person/123',
        },
        {
          type: 'birthday_reminder' as NotificationType,
          priority: NotificationPriority.NORMAL,
          title: 'Birthday Coming Up!',
          message: 'Mary Smith\'s birthday is in 3 days (January 15th).',
        },
        {
          type: 'research_note_assigned' as NotificationType,
          priority: NotificationPriority.HIGH,
          title: 'Research Task Assigned',
          message: 'You\'ve been assigned to research the Peterson family lineage.',
          actionUrl: '/research/456',
        },
        {
          type: 'content_approved' as NotificationType,
          priority: NotificationPriority.NORMAL,
          title: 'Photo Approved!',
          message: 'Your uploaded photo "Family Reunion 1985" has been approved.',
        },
      ];

      let count = 0;
      for (const params of testNotifications) {
        await NotificationService.createNotification({
          userId: user.uid,
          ...params,
        });
        count++;
      }

      setResult(`✅ Successfully created ${count} test notifications!\nCheck the notification bell in the header.`);
    } catch (err: any) {
      setError(`❌ Error: ${err.message}`);
      console.error('Multiple notification creation error:', err);
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You must be logged in to test notifications.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            🧪 Notification Tester
          </Typography>
          <Chip
            label="DEBUG TOOL - REMOVE BEFORE PRODUCTION"
            color="error"
            size="small"
            sx={{ mb: 2 }}
          />

          <Stack spacing={3} sx={{ mt: 3 }}>
            {/* Notification Type */}
            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={notificationType}
                label="Notification Type"
                onChange={(e) => setNotificationType(e.target.value as NotificationType)}
              >
                {notificationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value as NotificationPriority)}
              >
                {priorities.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Title */}
            <TextField
              fullWidth
              label="Title (optional - auto-generated if empty)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Leave empty to use default template"
            />

            {/* Message */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message (optional - auto-generated if empty)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Leave empty to use default template"
            />

            {/* Action URL */}
            <TextField
              fullWidth
              label="Action URL (optional)"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="/person/123 or /research/456"
            />

            {/* Buttons */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handleCreateNotification}
                fullWidth
              >
                Create Single Notification
              </Button>
              <Button
                variant="outlined"
                onClick={handleCreateMultiple}
                fullWidth
              >
                Create 5 Test Notifications
              </Button>
            </Stack>

            {/* Result */}
            {result && (
              <Alert severity="success" sx={{ whiteSpace: 'pre-line' }}>
                {result}
              </Alert>
            )}

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
                {error}
              </Alert>
            )}

            {/* Info */}
            <Alert severity="info">
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                Testing Tips:
              </Typography>
              <Typography variant="body2" component="div">
                • Leave Title/Message empty to see auto-generated templates<br />
                • Check the notification bell in the header (top-right)<br />
                • Notifications appear instantly via real-time listener<br />
                • Try different priorities to see color coding<br />
                • Use "Create 5 Test" to see animations and UI polish
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
